/**
 * FormModal Component
 * 
 * A reusable modal component for forms and additional information dialogs.
 * Provides consistent styling, overlay handling, and accessibility.
 * 
 * Design Principles:
 * - Clean white modal on dark overlay
 * - Consistent padding and spacing
 * - Accessible with proper ARIA attributes
 * - Smooth open/close animations
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import './FormModal.css';

export interface FormModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** Modal title */
  title: string;
  /** Modal content */
  children: React.ReactNode;
  /** Additional CSS class name */
  className?: string;
  /** Modal size variant */
  size?: 'small' | 'medium' | 'large';
}

const FormModal: React.FC<FormModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  className = '',
  size = 'medium',
}) => {
  // Handle overlay click to close
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key to close
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <div
      className="form-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="form-modal-title"
    >
      <div
        className={`form-modal-content form-modal-${size} ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="form-modal-header">
          <h2 id="form-modal-title">{title}</h2>
          <button
            className="form-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="form-modal-body">{children}</div>
      </div>
    </div>
  );
};

export default FormModal;

